import { BUTTON_REQUEST_TIMEOUT_MS } from '../constants/timeouts';

/**
 * Production GPU flow uses Cloud Functions (dispatch/cancel/retrain) + Firestore progress;
 * the browser does not need the RunPod URL in env.
 *
 * `getBackendBaseUrl()` is only for **local dev** (direct Flask) and optional **direct GPU** calls:
 * - GET /health, GET /pipeline/status/:id (recovery polling), legacy /viewer/:id splat fallback
 *
 * When `REACT_APP_BACKEND_MODE=runpod` and `REACT_APP_BACKEND_URL` is unset, this returns ''
 * (no pod URL in the bundle).
 */
export function isDispatchedGpuMode() {
  return (process.env.REACT_APP_BACKEND_MODE || 'local').toLowerCase() === 'runpod';
}

export function getBackendBaseUrl() {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:5000';
  }
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const mode = (process.env.REACT_APP_BACKEND_MODE || 'local').toLowerCase();
  const urlLocal = process.env.REACT_APP_BACKEND_URL_LOCAL?.replace(/\/$/, '');
  const urlEnv = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '');

  if (mode === 'runpod') {
    return urlEnv || '';
  }
  if (mode === 'local' && urlLocal) return urlLocal;
  if (!isLocalhost && urlEnv) return urlEnv;
  if (isLocalhost && urlLocal) return urlLocal;
  if (urlEnv) return urlEnv;
  return isLocalhost ? 'http://127.0.0.1:5000' : `${window.location.protocol}//${window.location.hostname}:5000`;
}

export function getBackendHealthUrl() {
  const baseUrl = getBackendBaseUrl();
  if (!baseUrl) return '';
  return `${baseUrl.replace(/\/$/, '')}/health`;
}

/**
 * Check if backend is reachable. Returns true if healthy, false if offline/error.
 * Use before starting uploads to avoid showing upload UI when backend is down.
 */
export async function checkBackendHealth() {
  if (isDispatchedGpuMode()) {
    const base = getBackendBaseUrl();
    if (!base) {
      return true;
    }
  }
  const url = getBackendHealthUrl();
  if (!url) return false;
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), BUTTON_REQUEST_TIMEOUT_MS);
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!res.ok) return false;
    const payload = await res.json().catch(() => ({}));
    if (typeof payload?.ready === 'boolean') {
      return payload.ready;
    }
    return true;
  } catch {
    return false;
  }
}
